import { Button, Checkbox, CheckboxGroup, Divider, Icon, Modal, Spinner, Text, TextArea, TextField } from '@nike/eds'
import { ConfirmationModal } from 'components/confirmation-modal'
import { useCallback, useMemo, useState } from 'react'
import { useAppSelector } from 'redux/store'
import { useFetchCountriesQuery } from 'api/country'
import {
  type FilterCondition,
  type GradingInstruction,
  newFilterCondition,
  validateCondition
} from 'types/Instruction'

import './GradingInstructionEditor.scss'
import { AuthRole } from 'auth/const'
import { FilterConditionRow } from './FilterConditions'

interface GradingInstructionEditorProps {
  gradingInstruction: GradingInstruction
  mode: 'Create' | 'Edit'
  onSave: (gradingInstruction: GradingInstruction) => void
  onDismiss: () => void
  onLoading: boolean
}

const initialErrors = { style: '', color: '', description: '', instruction: '', conditions: [] as Array<string | null> }

export const GradingInstructionEditor = ({ gradingInstruction, mode, onSave, onDismiss, onLoading }: GradingInstructionEditorProps) => {
  const user = useAppSelector((state) => state.globalState.user)

  const [errors, setErrors] = useState(initialErrors)
  const [instruction, setInstruction] = useState<GradingInstruction>(gradingInstruction)
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  // Loaded once for the modal so validateCondition can reject country codes
  // that aren't actual ISO-3166-1 entries (e.g. "ZZ"), matching the backend.
  // The CountryMultiSelect dropdown shares the same query (RTK keeps it cached).
  const { data: countries = [] } = useFetchCountriesQuery()
  const validCountryCodes = useMemo(
    () => new Set(countries.map(c => c.countryCode)),
    [countries]
  )

  const STYLE_REGEX = /^[a-zA-Z0-9]{6}$/
  const COLOR_REGEX = /^([a-zA-Z0-9]{3}|)$/
  const DESCRIPTION_MAX_LENGTH = 255
  const INSTRUCTION_MAX_LENGTH = 255

  const disableForm = user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION) ? false : true

  const save = () => {
    if (isValid(instruction)) {
      if (instruction.acceptableGrades.length === 0) {
        setConfirmationOpen(true)
        return
      }
      onSave(instruction)
    }
  }

  const isValid = (instruction: GradingInstruction): boolean => {
    const next = { ...initialErrors, conditions: [] as Array<string | null> }
    next.style = STYLE_REGEX.test(instruction.style) ? '' : '6 digits/letters'
    next.color = COLOR_REGEX.test(instruction.color) ? '' : 'Empty or 3 digits/letters'
    next.description = instruction.description.length > DESCRIPTION_MAX_LENGTH ? 'This field cannot be larger than 255 characters' : ''
    if (instruction.instruction === '') {
      next.instruction = 'This field is required'
    } else if (instruction.instruction.length > INSTRUCTION_MAX_LENGTH) {
      next.instruction = 'This field cannot be larger than 255 characters'
    }
    next.conditions = instruction.conditions.map(c => validateCondition(c, validCountryCodes))
    setErrors(next)
    const flatHasError = !!next.style || !!next.color || !!next.description || !!next.instruction
    const conditionsHaveError = next.conditions.some(e => e !== null)
    return !flatHasError && !conditionsHaveError
  }

  const handleChange = (field: string, value: string) => {
    setErrors(prevState => ({ ...prevState, [field]: '' }))
    setInstruction(prevState => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleCheckBox = async (id: string, checked: boolean) => {
    let grade: string | undefined
    switch (id) {
      case 'aGrade': grade = 'A'; break
      case 'bGrade': grade = 'B'; break
      case 'cGrade': grade = 'C'; break
      case 'sGrade': grade = 'S'; break
      case 'hash': grade = 'HASH'; break
    }
    if (checked) {
      setInstruction(prevState => ({
        ...prevState,
        acceptableGrades: [...prevState.acceptableGrades, grade!]
      }))
    } else {
      setInstruction(prevState => ({
        ...prevState,
        acceptableGrades: prevState.acceptableGrades.filter(g => g !== grade!)
      }))
    }
  }

  const addCondition = useCallback(() => {
    setInstruction(prev => ({ ...prev, conditions: [...prev.conditions, newFilterCondition()] }))
  }, [])

  const updateCondition = useCallback((idx: number, next: FilterCondition) => {
    setErrors(prev => {
      const arr = [...prev.conditions]
      arr[idx] = null
      return { ...prev, conditions: arr }
    })
    setInstruction(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => (i === idx ? next : c))
    }))
  }, [])

  const removeCondition = useCallback((idx: number) => {
    setErrors(prev => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== idx) }))
    setInstruction(prev => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== idx) }))
  }, [])

  const footerSlot = () => {
    if (onLoading) {
      return <Button size="medium" variant="primary" disabled><Spinner size="medium" /></Button>
    }
    if (disableForm) {
      return <Button size="medium" variant="primary" onClick={onDismiss}>Close</Button>
    } else {
      return <Button size="medium" variant="primary" onClick={save}>Save</Button>
    }
  }

  return (
    <Modal
      isOpen={true}
      headerSlot={mode === 'Create' ? 'Create grading instruction' : 'Edit grading instruction'}
      footerSlot={<div>{footerSlot()}</div>}
      onDismiss={onDismiss}
      hideFade={true}>
      <div className="instruction-modal">
        <div className="product-code">
          <div className="style w-1/5">
            <TextField
              id="style"
              type="text"
              label="Style"
              disabled={disableForm}
              value={instruction.style}
              maxChars={6}
              onChange={(e) => {
                handleChange('style', e.target.value)
              }}
              hasErrors={errors.style !== ''}
              errorMessage={errors.style}
            />
          </div>
          <div className="w-1/5">
            <TextField
              id="color"
              type="text"
              label="Color"
              disabled={disableForm}
              value={instruction.color}
              maxChars={3}
              onChange={(e) => {
                handleChange('color', e.target.value)
              }}
              hasErrors={errors.color !== ''}
              errorMessage={errors.color}
            />
          </div>
        </div>

        <Divider />

        <TextArea
          showCount={true}
          id="description"
          label="Description"
          disabled={disableForm}
          subtitle="Text to provide context and legibility within the admin UI"
          value={instruction.description}
          maxChars={255}
          minRows={2}
          onChange={(e) => { handleChange('description', e.target.value) }}
          hasErrors={errors.description !== ''}
          errorMessage={errors.description}
        />
        <TextArea
          showCount={true}
          id="instruction"
          label="Special Instruction"
          disabled={disableForm}
          subtitle="What will actually displayed during unit-handling"
          value={instruction.instruction}
          maxChars={255}
          minRows={2}
          onChange={(e) => { handleChange('instruction', e.target.value) }}
          hasErrors={errors.instruction !== ''}
          errorMessage={errors.instruction}
        />
        <CheckboxGroup
          id="availableGrades"
          label="Available grades"
          name="availableGrades"
          orientation='horizontal'
          className={'mb-6'}
          onChange={(e) => { handleCheckBox(e.target.id, e.target.checked) }}
        >
          <Checkbox label="A" id="aGrade" checked={instruction.acceptableGrades.includes('A')} disabled={disableForm} />
          <Checkbox label="B" id="bGrade" checked={instruction.acceptableGrades.includes('B')} disabled={disableForm} />
          <Checkbox label="C" id="cGrade" checked={instruction.acceptableGrades.includes('C')} disabled={disableForm} />
          <Checkbox label="S" id="sGrade" checked={instruction.acceptableGrades.includes('S')} disabled={disableForm} />
          <Checkbox label="Hash" id="hash" checked={instruction.acceptableGrades.includes('HASH')} disabled={disableForm} />
        </CheckboxGroup>

        <Divider />

        <div className="filter-conditions-section">
          <Text font="title-6">Filter Conditions</Text>
          <Text font="body-3" color="text-secondary">
            All three are optional. Empty = applies to all values. Ship To and Country are mutually exclusive — set one or the other, not both.
          </Text>

          {instruction.conditions.map((cond, idx) => (
            <FilterConditionRow
              key={idx}
              value={cond}
              disabled={disableForm}
              error={errors.conditions[idx] ?? null}
              onChange={(next) => { updateCondition(idx, next) }}
              onRemove={() => { removeCondition(idx) }}
            />
          ))}

          {!disableForm && (
            <div>
              <Button
                variant="secondary"
                size="small"
                beforeSlot={<Icon name="Plus" size="s" />}
                onClick={addCondition}
              >Add filter condition</Button>
            </div>
          )}
        </div>
      </div>
      {confirmationOpen &&
        <ConfirmationModal
          title="No grade selected"
          message="You have not selected any grades. This means that this material cannot be processed during unit handling. Are you sure you want to proceed?"
          onCancel={() => {
            setConfirmationOpen(false)
          }}
          onTrigger={() => {
            setConfirmationOpen(false)
            if (isValid(instruction)) {
              onSave(instruction)
            }
          }}
          confirmText={'Save'}
        />
      }
    </Modal>
  )
}
